/**
 * PlatformIO — one interface, two implementations (web + desktop).
 *
 * Feature/UI code depends ONLY on this interface (never directly on Tauri or
 * browser APIs). See contracts/client-integration.md.
 */
import type { FtpConfig } from "./types.js";

export interface PickedFile {
  name: string;
  /** Web: the picked path is not exposed; desktop: absolute path. */
  path?: string;
  bytes: Uint8Array;
  mime?: string;
}

export interface PickFilesOptions {
  accept?: string[]; // e.g. ["image/png", "image/jpeg"]
  multiple?: boolean;
  /** Validation guard rails (Constitution VI: size/type-validated uploads). */
  maxBytes?: number;
  /**
   * Optional folder-import progress. The desktop `pickDirectory` reads files with a
   * SEQUENTIAL loop (parallel IPC reads ballooned memory — see docs/coding-rules.md), so a
   * huge folder used to freeze the UI with no feedback. `total` is known up front (the Rust
   * directory walk returns the full list in one shot); `done` ticks once per file — INCLUDING
   * files skipped by `accept`/`maxBytes`, so `done` reaches `total`. Web reports per-file too
   * (it already has every File in hand); either platform may omit it entirely.
   */
  onProgress?: (done: number, total: number) => void;
}

/**
 * Canonical image MIME accept list for file pickers (templates, designs, references).
 * Broad on purpose so the native dialog (desktop) and `<input accept>` (web) allow every
 * common image type — not just PNG. Desktop maps these to real file extensions via
 * `acceptToExtensions` (e.g. image/jpeg → .jpg AND .jpeg). Shared so every picker behaves
 * identically.
 */
export const IMAGE_ACCEPT: string[] = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/bmp",
  "image/tiff",
  "image/heic",
  "image/heif",
  "image/avif",
];

/**
 * WS-W — why a baked-asset download has its own capability.
 *
 * On 2026-09-16 the desktop app (v0.2.93, macOS 27) failed EVERY baked-asset download from
 * `https://assets.untangle.today/template-refs/…` for over an hour with WebKit's status-less
 * `TypeError: Load failed`, while `curl`, `URLSession`, Safari and a stand-alone WKWebView on
 * the same Mac fetched the same objects fine. CORS (preflight 204), the desktop CSP (the host is
 * in `connect-src`), Screen Time, Bitdefender, ITP and HTTP/3 were each ruled out with evidence.
 * What was left is state inside the app's own long-lived WebKit networking process — a stranded
 * pooled connection every subsequent `fetch` to that host is multiplexed onto.
 *
 * The bytes that carry the product's mockup (background, texture, shell) must not depend on the
 * web view's connection pool, its CSP allow-list or a content filter layered onto it. DESKTOP
 * ONLY — absent on web, where `fetch` is the only transport there is, so callers feature-detect
 * exactly as they do for `pickSaveDirectory` / `readExportedFile`.
 */

/**
 * Why a `downloadBytes` attempt never produced an HTTP response. A closed union because this is
 * precisely what the operator's log was missing: every desktop failure used to read `Load
 * failed`, which names neither the url nor the cause. Mirrored 1:1 by the Rust classifier in
 * `apps/desktop/src-tauri/src/download.rs`.
 *
 *  - `policy`    — refused BEFORE any socket: not HTTPS, or a host that is not on the download
 *                  allow-list (the command is not a general-purpose proxy for the web view).
 *  - `dns`       — the host did not resolve.
 *  - `connect`   — the TCP connection failed or timed out.
 *  - `tls`       — the TLS handshake or certificate validation failed.
 *  - `timeout`   — the whole request passed `timeoutMs`.
 *  - `redirect`  — a redirect left the original host/scheme, or ran too long.
 *  - `body`      — the response started and then failed mid-stream.
 *  - `too-large` — the body passed the platform's size cap.
 *  - `request`   — anything the transport could not classify (incl. a broken IPC bridge).
 */
export type DownloadErrorKind =
  | "policy"
  | "dns"
  | "connect"
  | "tls"
  | "timeout"
  | "redirect"
  | "body"
  | "too-large"
  | "request";

/** The host ANSWERED: an HTTP status arrived, whatever it says. */
export interface DownloadBytesResponse {
  /** The real HTTP status (200…599). Its PRESENCE is the contract: it is what tells a retry
   *  ladder apart a final answer (404) from a blip (503) from no answer at all. */
  status: number;
  statusText?: string;
  /**
   * The body on a 2xx; EMPTY on every other status.
   *
   * R2 answers a missing object with an XML error document that reads as perfectly valid bytes.
   * Those bytes were once stored AS the baked asset, so a missing file silently became a corrupt
   * one — the platform drops a non-2xx body rather than passing it up.
   */
  bytes: Uint8Array;
}

/** NO HTTP response ever arrived — a NAMED transport failure, reported as a value. */
export interface DownloadBytesFailure {
  kind: DownloadErrorKind;
  /** Operator-facing reason. ALWAYS names the URL and what the transport actually did. */
  error: string;
}

/**
 * Exactly one of the two shapes. The absence of `status` on the failure arm is deliberate: it is
 * the same distinction `fetch` draws between resolving with a `Response` and rejecting, so a
 * retry ladder written against `fetch` keeps its meaning over this seam without changing.
 */
export type DownloadBytesResult = DownloadBytesResponse | DownloadBytesFailure;

/** Narrow a `DownloadBytesResult` to the "no response at all" arm. */
export function isDownloadFailure(r: DownloadBytesResult): r is DownloadBytesFailure {
  return "error" in r;
}

/** A virtual export tree: relative path -> file bytes. */
export interface ExportFile {
  /** Path relative to the export root, using buildRef() conventions. */
  relPath: string;
  bytes: Uint8Array;
  mime?: string;
}

export interface ExportBundle {
  /** Suggested root folder / zip name. */
  name: string;
  files: ExportFile[];
  /** Desktop: pre-chosen destination folder — write here WITHOUT prompting. Web ignores it. */
  destDir?: string;
}

/** Outcome of `exportBundle`: how many files hit disk, and whether the user cancelled the
 *  destination dialog (desktop) — a cancel writes nothing and must not be reported as saved. */
export interface ExportBundleResult {
  written: number;
  cancelled?: boolean;
  /**
   * WHERE the files actually landed (the bundle root, e.g. `<chosen folder>/Mockups`).
   *
   * The result used to report only a count, so "Saved 12 mockups" could not be checked against
   * the folder the operator believed they had chosen — and when the two disagreed there was no
   * way to tell from the UI. Desktop only; web has no filesystem path.
   */
  destPath?: string;
  /** Set when revealing `destPath` in the file manager failed, so the UI can say so instead of
   *  the folder silently not opening. */
  openError?: string;
}

/** Minimal key-value store, present on both platforms. */
export interface KeyValueStore {
  get<T = unknown>(key: string): Promise<T | undefined>;
  set<T = unknown>(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
  keys(prefix?: string): Promise<string[]>;
}

/**
 * A tiny "SQLite-like" record store. Both desktop and web back this with the
 * shared IndexedDB store (createIdbLocalStore). Used for the persistent job
 * queue + telemetry buffer (010). Intentionally narrow — not a full SQL surface.
 */
export interface RecordStore {
  put(collection: string, id: string, record: unknown): Promise<void>;
  get<T = unknown>(collection: string, id: string): Promise<T | undefined>;
  all<T = unknown>(collection: string): Promise<T[]>;
  remove(collection: string, id: string): Promise<void>;
  clear(collection: string): Promise<void>;
}

export interface LocalStore {
  kv: KeyValueStore;
  records: RecordStore;
}

export type ConnectivityListener = (online: boolean) => void;

export interface PlatformIO {
  /**
   * Reveal a folder (or a file's folder) in the OS file manager. DESKTOP ONLY — absent on web,
   * so callers must feature-detect. Exposed on the shared surface because the UI needs to offer
   * "Open folder" after an export; it previously existed only inside the desktop adapter, where
   * no panel could reach it.
   */
  openPath?(path: string): Promise<void>;

  /**
   * WS-HF shared login — open a page OUTSIDE the app: the SYSTEM browser on the desktop, a new
   * tab on the web. Used for the company Higgsfield sign-in page. Refuses (rejects) any URL that
   * `isAllowedExternalUrl` does not accept — it is not a general "open a link" surface.
   * Optional so a test double or an older bridge can leave it out; callers feature-detect.
   */
  openExternal?(url: string): Promise<void>;

  /**
   * Read back a file this app already WROTE under `root` (WS-H2 / AUD-3 F-15 / LB-2).
   * DESKTOP ONLY — absent on web, where there is no folder to read back from, so callers must
   * feature-detect. Implemented with the existing `joinPath` + `readFile` bridge, i.e. the same
   * `validated_path` confinement (NAT-1) and no new authorization command. Returns `null` on any
   * error rather than throwing: a moved or deleted file must surface as a `missing` export row.
   */
  readExportedFile?(root: string, relPath: string): Promise<Uint8Array | null>;

  /**
   * Create `dir` (and its parents) if it is not already there — the "can this run actually
   * write where the operator pointed it?" probe (WS-O / AUD-8 LB-2).
   *
   * DESKTOP ONLY — absent on web, so callers must feature-detect, and an absent probe means
   * `"unknown"`, never `"unwritable"`. Implemented with the existing `mkdirp` bridge command,
   * i.e. the same `validated_path` confinement (NAT-1) and no new authorization command.
   * Idempotent, so probing leaves nothing behind.
   *
   * It REJECTS with the platform's own reason rather than swallowing it: "path is outside every
   * folder you picked" (an unplugged external drive, or a folder chosen in an older build) and
   * "No such file or directory" are different recoveries, and the operator is told which.
   */
  ensureDir?(dir: string): Promise<void>;

  /**
   * Download `url` and return its bytes WITHOUT the web view (WS-W — see `DownloadErrorKind`).
   *
   * DESKTOP ONLY — absent on web, so callers must feature-detect; an absent capability means
   * "use `fetch`", never "downloads are unavailable". Desktop implements it with the Rust
   * `genz_download_bytes` command (`reqwest`, HTTPS only, the CSP's asset hosts, a size cap,
   * no cross-host redirects, no pooled connections).
   *
   * It does NOT reject for a network failure — a failure is a VALUE (`DownloadBytesFailure`),
   * so the caller's retry ladder can tell "no response" from "a response that says 404" without
   * parsing an exception message. It may still reject if the bridge itself is broken, and a
   * caller must treat that exactly as it treats a thrown `fetch` error.
   *
   * `timeoutMs` is the whole-request deadline (the shared `DOWNLOAD_TIMEOUT_MS`) and is the ONLY
   * way to end a download early. There is deliberately no `AbortSignal`: the request runs on the
   * far side of the IPC and Tauri gives the web view no way to cancel a command already in
   * flight, so a signal here could only be declared and ignored — the caller would write
   * cancellation it does not get, and a hydrate it thinks it cancelled would keep running to the
   * deadline either way.
   */
  downloadBytes?(url: string, opts: { timeoutMs: number }): Promise<DownloadBytesResult>;

  /** Native dialog (desktop) / <input> or File System Access (web). */
  pickFiles(opts?: PickFilesOptions): Promise<PickedFile[]>;

  /** Pick a FOLDER and return every matching file under it, with `path` set to the
   *  relative path (web) / absolute path (desktop) so the source subfolder is preserved
   *  as a design category (spec 014). */
  pickDirectory(opts?: PickFilesOptions): Promise<PickedFile[]>;

  /** Desktop: prompt once for an OUTPUT folder and return its absolute path (null = cancel),
   *  so callers can choose a save destination BEFORE rendering and write to it without a
   *  second prompt. Web leaves this undefined — there is no silent folder write, so the
   *  caller falls back to a zip download. */
  pickSaveDirectory?(): Promise<string | null>;

  /** Decode image bytes to RGBA pixels (web: canvas; desktop: Tauri). Used to resolve a
   *  background image / brand logo into the engine's local raster inputs (spec 015).
   *  rgba is the canvas `ImageData.data` (Uint8ClampedArray) returned ZERO-COPY — never
   *  `Array.from(data)`, which built a 4·w·h-element JS number[] (~1 GB for a 4000² image)
   *  on the UI thread (docs/coding-rules.md "Maximums"). Consumers index rgba[i] as before. */
  decodeImage(bytes: Uint8Array): Promise<{ width: number; height: number; rgba: Uint8ClampedArray }>;

  /** Desktop: write folder tree under a chosen dir. Web: download a zip.
   *  Returns what actually happened so callers can distinguish "wrote N files" from a
   *  CANCELLED folder dialog — a cancel used to resolve void and the UI reported
   *  "Saved N mockups" with zero bytes on disk. `void` (legacy impls) ⇒ assume written. */
  exportBundle(bundle: ExportBundle): Promise<ExportBundleResult | void>;

  /** Single-file save (desktop dialog) / download (web). */
  download(name: string, bytes: Uint8Array, mime?: string): Promise<void>;

  /** Desktop only: upload rendered files to the configured FTP server (Settings → FTP).
   *  Bytes go straight from the desktop to the server; the cloud never sees pixels. Web
   *  throws (browsers can't open raw FTP sockets). Returns the count uploaded. */
  ftpUpload(config: FtpConfig, files: { name: string; bytes: Uint8Array }[]): Promise<number>;

  /** Persistent local store (queue + telemetry buffer + caches). */
  localStore: LocalStore;

  /** Stable per-device identifier for activation (009). */
  deviceFingerprint(): Promise<string>;

  /**
   * Device-encrypted secret store for short, sensitive strings (e.g. auth tokens) that must
   * NOT sit in the XSS-readable webview `localStorage`.
   *
   * Desktop backs this with the native AES-256-GCM store under the device key. **Web has no
   * such primitive**, so the web implementation throws (set/delete) / returns `undefined`
   * (get); callers MUST wrap these in try/catch and fall back to their existing storage.
   * Values are never logged.
   */
  secretGet(key: string): Promise<string | undefined>;
  secretSet(key: string, value: string): Promise<void>;
  secretDelete(key: string): Promise<void>;

  /** Connectivity for the offline UX + telemetry buffering (010). */
  isOnline(): boolean;
  onConnectivityChange(cb: ConnectivityListener): () => void;

  /** "web" | "desktop" — for the rare branch the UI legitimately needs. */
  readonly kind: "web" | "desktop";
}

// ---------------------------------------------------------------------------
// Current-platform registry
// ---------------------------------------------------------------------------
//
// The web/desktop entrypoints construct exactly one PlatformIO at startup via
// `createWebPlatform` / `createDesktopPlatform`, which register it here. Modules that
// can't take `io` through props (e.g. the zustand session store, which needs the secret
// store for desktop token persistence) read it through `currentPlatform()`. Returns
// `undefined` until the entrypoint has constructed the platform — callers must handle
// that (and any error) by falling back to their default behavior.

let _current: PlatformIO | undefined;

/** Register the process-wide PlatformIO (called by the platform factories). */
export function setCurrentPlatform(io: PlatformIO): void {
  _current = io;
}

/** The registered PlatformIO, or `undefined` if none has been constructed yet. */
export function currentPlatform(): PlatformIO | undefined {
  return _current;
}
